import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import {
  AlertCircle,
  CheckCircle,
  Copy,
  DollarSign,
  ExternalLink,
  Filter,
  Globe,
  Search,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { publisherApi } from '../lib/api'

export function Campaigns() {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'paused'>('all')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: publisherApi.getCampaigns,
  })

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const copyTrackingLink = (campaign: any) => {
    navigator.clipboard.writeText(campaign.trackingUrl)
    setCopiedId(campaign.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const getPayoutTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cpa: 'Cost Per Action',
      cpl: 'Cost Per Lead',
      cps: 'Cost Per Sale',
      revshare: 'Revenue Share',
    }
    return labels[type] || type.toUpperCase()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Available Campaigns</h1>
        <p className="text-gray-600 mt-1">
          Choose campaigns to promote and get your tracking links
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">All Campaigns</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>
      </div>

      {/* Campaign Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCampaigns.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">No campaigns found</h3>
            <p className="text-gray-600 mt-2">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          filteredCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Campaign Header */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                      {campaign.name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {campaign.description || 'No description available'}
                    </p>
                  </div>
                  <div
                    className={clsx(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      campaign.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800',
                    )}
                  >
                    {campaign.status === 'active' ? (
                      <div className="flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <XCircle className="w-3 h-3 mr-1" />
                        Paused
                      </div>
                    )}
                  </div>
                </div>

                {/* Campaign Details */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="w-4 h-4 mr-1" />
                      <span>Payout</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      ${campaign.payout.toFixed(2)} / {getPayoutTypeLabel(campaign.payoutType)}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <Globe className="w-4 h-4 mr-1" />
                      <span>Countries</span>
                    </div>
                    <div className="text-sm text-gray-900">
                      {campaign.countries.length > 0
                        ? campaign.countries.join(', ')
                        : 'All Countries'}
                    </div>
                  </div>
                </div>

                {/* Tracking Link */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">Tracking Link</p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={campaign.trackingUrl}
                      readOnly
                      className="flex-1 px-3 py-2 text-xs bg-white border border-gray-300 rounded-md font-mono"
                    />
                    <button
                      onClick={() => copyTrackingLink(campaign)}
                      className={clsx(
                        'p-2 rounded-md transition-colors',
                        copiedId === campaign.id
                          ? 'bg-green-100 text-green-600'
                          : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50',
                      )}
                      title="Copy tracking link"
                    >
                      {copiedId === campaign.id ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Campaign Actions */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <a
                    href={campaign.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Preview Landing Page
                  </a>
                  <span className="text-xs text-gray-500">{campaign.category}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-indigo-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600">Total Campaigns</p>
            <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Active Campaigns</p>
            <p className="text-2xl font-bold text-gray-900">
              {campaigns.filter((c) => c.status === 'active').length}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Average Payout</p>
            <p className="text-2xl font-bold text-gray-900">
              $
              {campaigns.length > 0
                ? (campaigns.reduce((sum, c) => sum + c.payout, 0) / campaigns.length).toFixed(2)
                : '0.00'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
